/**
 * Org area — grid of the org's widget instances + a quick "create instance"
 * form. Each card links to the full config editor. The plaintext secret key is
 * surfaced once at creation (shown inline so the admin can copy it before it's
 * gone for good).
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { WidgetInstance, WidgetInstanceSecret } from '@hotline/contracts';

import { useAuth } from '../../auth/AuthContext.js';
import { useOrgScope } from '../../org/OrgScopeContext.js';
import { CopyBlock } from '../../components/ui.js';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
  StatusPill,
  Toolbar,
} from '../../components/ui/index.js';
import { Icon } from '../../components/ui/icons.js';
import { useAsync, toMessage } from '../../lib/useAsync.js';

/** Format an ISO timestamp as a short, human "Created" date. */
function formatCreated(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * A single widget-instance card (Claude Design `.wcard`): glyph + name + origin,
 * the public key with a copy affordance, and a footer with the created date and
 * a link into the config editor.
 */
function WidgetCard({ wi }: { wi: WidgetInstance }): ReactElement {
  const origin = wi.allowedOrigins[0];
  return (
    <Card
      data-instance-id={wi.id}
      className="flex flex-col gap-[14px] p-[18px] transition-colors hover:border-border-strong"
    >
      {/* top: glyph + name/origin + status */}
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="grid size-[38px] flex-shrink-0 place-items-center rounded-md bg-primary text-white"
        >
          <Icon.widgets size={17} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14.5px] font-bold text-text">{wi.name}</div>
          {origin ? (
            <div className="mt-0.5 truncate font-mono text-xs text-muted">
              {origin}
            </div>
          ) : null}
        </div>
        <StatusPill status={wi.status} />
      </div>

      {/* public key row */}
      <div className="flex items-center gap-2 rounded-sm border border-border bg-surface-2 py-2 pl-[11px] pr-2 font-mono text-xs">
        <code className="min-w-0 flex-1 truncate tracking-[0.04em] text-muted">
          {wi.publicKey}
        </code>
        <CopyButton value={wi.publicKey} />
      </div>

      {/* stats: capture level (real data has no traffic counters) */}
      <div className="flex gap-[22px] pt-0.5">
        <div>
          <div className="text-[17px] font-extrabold tabular-nums text-text">
            L{wi.captureLevel}
          </div>
          <div className="text-[11px] font-semibold text-faint">Capture level</div>
        </div>
      </div>

      {/* footer: created + configure */}
      <div className="flex items-center justify-between border-t border-border pt-[13px]">
        <span className="text-xs text-faint">
          {formatCreated(wi.createdAt)
            ? `Created ${formatCreated(wi.createdAt)}`
            : null}
        </span>
        <Link
          to={`/org/instances/${wi.id}`}
          className="inline-flex h-[27px] items-center gap-[7px] rounded-sm px-2.5 text-xs font-semibold text-muted transition-colors hover:bg-surface-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          <Icon.sliders size={14} />
          Configure
        </Link>
      </div>
    </Card>
  );
}

/** The `.wcard-key .kbtn` copy affordance — copies a value to the clipboard. */
function CopyButton({ value }: { value: string }): ReactElement {
  const [copied, setCopied] = useState(false);
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label="Copy public key"
      className="flex-shrink-0 rounded-[5px] p-[3px] text-faint transition-colors hover:bg-surface-3 hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {copied ? <Icon.check size={14} /> : <Icon.copy size={14} />}
    </button>
  );
}

export default function WidgetInstancesPage(): ReactElement {
  const { client } = useAuth();
  const { query } = useOrgScope();

  const instances = useAsync<WidgetInstance[]>(
    () => client.api.listWidgetInstances(query),
    [client, query.orgId],
  );
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<WidgetInstanceSecret | null>(
    null,
  );

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    try {
      const created = await client.api.createWidgetInstance({ name }, query);
      if (created.secretKey && created.publicKey) {
        setRevealedSecret({
          id: created.id,
          publicKey: created.publicKey,
          secretKey: created.secretKey,
        });
      }
      setName('');
      instances.reload();
    } catch (err) {
      setError(toMessage(err));
    }
  };

  return (
    <>
      <PageHeader
        title="Widget instances"
        description="Embeddable feedback surfaces across your sites. Each instance has its own keys and capture level."
      />

      <form
        className="mb-6 flex flex-col gap-3"
        onSubmit={create}
        aria-label="Create widget instance"
      >
        <Toolbar>
          <Input
            className="w-full sm:max-w-xs"
            aria-label="Instance name"
            placeholder="New widget instance name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Button type="submit">
            <Icon.plus size={15} />
            Create instance
          </Button>
        </Toolbar>
        {error ? <Alert>{error}</Alert> : null}
      </form>

      {revealedSecret ? (
        <Card role="alert" className="mb-6 border-warn/40 bg-warn-soft">
          <Card.Body className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="grid size-[34px] flex-shrink-0 place-items-center rounded-md bg-warn-soft text-warn"
              >
                <Icon.lock size={16} />
              </span>
              <div>
                <strong className="text-sm font-semibold text-text">
                  Save this secret key now — it is shown only once.
                </strong>
                <p className="mt-0.5 text-xs text-muted">
                  Store it somewhere safe; we never display it again.
                </p>
              </div>
            </div>
            <CopyBlock label="Public key" code={revealedSecret.publicKey} />
            <CopyBlock label="Secret key" code={revealedSecret.secretKey} />
            <div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setRevealedSecret(null)}
              >
                I&apos;ve saved it
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : null}

      {instances.loading ? (
        <div role="status" className="py-2">
          <Spinner label="Loading widget instances…" />
        </div>
      ) : null}
      {instances.error ? (
        <div className="mb-6">
          <Alert>{instances.error}</Alert>
        </div>
      ) : null}

      {instances.data && instances.data.length === 0 ? (
        <EmptyState
          title="No widget instances yet"
          description="Create one to get an install snippet."
        />
      ) : null}

      {instances.data && instances.data.length > 0 ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
          {instances.data.map((wi) => (
            <WidgetCard key={wi.id} wi={wi} />
          ))}
        </div>
      ) : null}
    </>
  );
}
